import React, { useState, useEffect, useMemo } from 'react';
import Modal from './Modal';
import { toast } from 'react-toastify';
import axios from 'axios';
import { header } from './header.encry';
import StoreService from './StoreService';
import { fetchPlateforms } from './PlateformService';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;

const GlovoToggleProductModal = ({ products, onClose, onSuccess }) => {
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [availability, setAvailability] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [glovoPortalStores, setGlovoPortalStores] = useState([]);

  const isMultiProduct = Array.isArray(products) && products.length > 1;
  const productsList = Array.isArray(products) ? products : (products ? [products] : []);

  useEffect(() => {
    fetchGlovoPortalStores();
  }, []);

  const fetchGlovoPortalStores = async () => {
    try {
      // Fetch all platforms
      const allPlateforms = await fetchPlateforms();
      
      // Filter for Glovo portal platforms
      const glovoPortalPlateforms = allPlateforms.filter(platform => {
        const isGlovo = platform.type?.toLowerCase() === 'glovo';
        const isPortal = platform.additionnal_data?.isportal === true || 
                        platform.additional_data?.isportal === true;
        return isGlovo && isPortal;
      });

      // Fetch all stores
      const allStores = await StoreService.fetchStores();

      // Get store IDs from Glovo portal platforms
      const portalStoreIds = new Set();
      glovoPortalPlateforms.forEach(platform => {
        if (Array.isArray(platform.store_ids)) {
          platform.store_ids.forEach(storeId => {
            // Handle both ObjectId format and string format
            const id = typeof storeId === 'object' ? storeId.$oid || storeId._id : storeId;
            if (id) portalStoreIds.add(String(id));
          });
        }
      });

      // Filter stores that are in the portal's store_ids array
      const portalStores = allStores.filter(store => {
        const storeId = String(store._id || store.id);
        return portalStoreIds.has(storeId);
      });

      setGlovoPortalStores(portalStores);
    } catch (error) {
      console.error('Error fetching Glovo portal stores:', error);
      toast.error('Impossible de charger les magasins Glovo Portal');
    }
  };

  const handleStoreToggle = (storeId) => {
    setSelectedStores(prev => {
      if (prev.includes(storeId)) {
        return prev.filter(id => id !== storeId);
      } else {
        return [...prev, storeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedStores.length === glovoPortalStores.length) {
      setSelectedStores([]);
    } else {
      setSelectedStores(glovoPortalStores.map(store => store._id));
    }
  };

  const handleSubmit = async () => {
    if (productsList.length === 0) {
      toast.error('Aucun produit sélectionné');
      return;
    }

    if (selectedStores.length === 0) {
      toast.error('Veuillez sélectionner au moins un magasin');
      return;
    }

    setIsSubmitting(true);

    try {
      const results = [];
      const errors = [];

      // Process each product
      for (const product of productsList) {
        try {
          const response = await axios.post(
            `${API_URL}/glovo-portal/toggle-product`,
            {
              product_id: product._id,
              store_ids: selectedStores,
              availability: availability
            },
            header()
          );

          if (response.data.success) {
            results.push({
              product: product.name,
              ...response.data
            });
          } else {
            // API returned success: false
            errors.push({
              product: product.name,
              error: response.data.error || 'Erreur inconnue'
            });
          }
        } catch (error) {
          errors.push({
            product: product.name,
            error: error.response?.data?.error || error.message
          });
        }
      }

      // Show error notifications
      if (errors.length > 0) {
        errors.forEach(err => {
          toast.error(`${err.product}: ${err.error}`, {
            autoClose: 5000,
          });
        });
      }

      // Show success notifications
      if (results.length > 0) {
        const successCount = results.reduce((sum, r) => sum + (r.totalProcessed || 0), 0);
        const errorCount = results.reduce((sum, r) => sum + (r.totalErrors || 0), 0);
        
        if (errorCount === 0) {
          toast.success(
            `${successCount} produit(s) ${availability ? 'activé(s)' : 'désactivé(s)'} avec succès`
          );
        } else {
          toast.warning(
            `${successCount} produit(s) traité(s), ${errorCount} erreur(s) rencontrée(s)`
          );
        }

        // Call onSuccess callback
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      }

      // Close modal only if no errors occurred
      if (errors.length === 0 && results.length > 0) {
        closeModal();
      }
    } catch (error) {
      console.error('Error toggling product:', error);
      toast.error('Erreur lors de la modification de la disponibilité');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setSelectedStores([]);
    setAvailability(true);
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <Modal
      modalId="glovoToggleProductModal"
      title={isMultiProduct ? `Gérer la disponibilité (${productsList.length} produits)` : 'Gérer la disponibilité Glovo'}
      submitLabel={isSubmitting ? 'Traitement...' : (availability ? 'Activer' : 'Désactiver')}
      submitDisabled={isSubmitting || selectedStores.length === 0}
      onSubmit={handleSubmit}
      onClose={closeModal}
    >
      <div className="mb-3">
        {isMultiProduct ? (
          <div className="alert alert-info">
            <strong>{productsList.length} produits sélectionnés</strong>
            <ul className="mb-0 mt-2" style={{ maxHeight: '100px', overflowY: 'auto' }}>
              {productsList.map(product => (
                <li key={product._id}>{product.name}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="alert alert-info">
            <strong>Produit:</strong> {productsList[0]?.name || 'Non défini'}
          </div>
        )}
      </div>

      <div className="mb-3">
        <label className="form-label">Action</label>
        <div className="btn-group w-100" role="group">
          <button
            type="button"
            className={`btn ${availability ? 'btn-success' : 'btn-outline-success'}`}
            onClick={() => setAvailability(true)}
            disabled={isSubmitting}
          >
            <i className="fa-solid fa-check-circle me-2"></i>
            Activer
          </button>
          <button
            type="button"
            className={`btn ${!availability ? 'btn-danger' : 'btn-outline-danger'}`}
            onClick={() => setAvailability(false)}
            disabled={isSubmitting}
          >
            <i className="fa-solid fa-ban me-2"></i>
            Désactiver
          </button>
        </div>
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label mb-0">Magasins Glovo Portal</label>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleSelectAll}
            disabled={isSubmitting || glovoPortalStores.length === 0}
          >
            {selectedStores.length === glovoPortalStores.length ? 'Désélectionner tout' : 'Sélectionner tout'}
          </button>
        </div>

        {glovoPortalStores.length === 0 ? (
          <div className="alert alert-warning">
            Aucun magasin Glovo Portal disponible. Vérifiez qu'une plateforme Glovo Portal est configurée avec des magasins.
          </div>
        ) : (
          <div className="border rounded p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {glovoPortalStores.map(store => (
              <div key={store._id} className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`store-${store._id}`}
                  checked={selectedStores.includes(store._id)}
                  onChange={() => handleStoreToggle(store._id)}
                  disabled={isSubmitting}
                />
                <label className="form-check-label" htmlFor={`store-${store._id}`}>
                  <strong>{store.name}</strong>
                  {store.storeCode && <span className="text-muted ms-2">({store.storeCode})</span>}
                  {store.globalEntityId && (
                    <span className="badge bg-info ms-2">{store.globalEntityId}</span>
                  )}
                  {store.global_entity_id && (
                    <span className="badge bg-info ms-2">{store.global_entity_id}</span>
                  )}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="alert alert-secondary">
        <small>
          <strong>Résumé:</strong> {availability ? 'Activer' : 'Désactiver'} {productsList.length} produit(s) 
          dans {selectedStores.length} magasin(s)
        </small>
      </div>
    </Modal>
  );
};

export default GlovoToggleProductModal;
